// Senior certification of legal aid applications, from the browser's side.
// The rules live in api/certifications.js; this is how the portal pages talk to it and how
// the application decides whether it may be lodged.

/** Same answers -> same fingerprint, whatever order the fields were filled in. */
export function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort()
      .filter((key) => value[key] !== undefined)
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

/** A fingerprint of the application's answers. Any edit after certification changes it. */
export async function applicationFingerprint(formData) {
  const bytes = new TextEncoder().encode(stableStringify(formData || {}));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Whether the application can be lodged (emailed, printed or downloaded), and what to tell
 * the lawyer. `status` is the GET /api/certifications response, or null if it isn't known yet.
 */
export function lodgingState(status, fingerprint) {
  if (!status) return { state: "checking", canLodge: false, message: "Checking certification…" };
  if (status.unavailable) return { state: "not_required", canLodge: true, message: "" };
  if (status.error) return { state: "error", canLodge: false, message: status.error };
  if (!status.required) return { state: "not_required", canLodge: true, message: "" };

  const c = status.certification;
  if (!c) return { state: "not_sent", canLodge: false, message: "This application must be certified before it can be lodged." };
  const sameAnswers = c.data_hash === fingerprint;
  if (c.status === "pending") {
    return sameAnswers
      ? { state: "pending", canLodge: false, message: `Waiting for certification (sent ${formatWhen(c.submitted_at)}).` }
      : { state: "changed", canLodge: false, message: "The application has changed since it was sent for certification. Send it again." };
  }
  if (c.status === "returned") {
    return { state: "returned", canLodge: false, message: `Returned by ${c.decided_by || "the certifier"}: ${c.decision_note || "changes needed"}` };
  }
  if (c.status === "certified") {
    if (!sameAnswers) {
      return { state: "changed", canLodge: false, message: "The application has changed since it was certified. Send it for certification again." };
    }
    const certified = `Certified by ${c.decided_by || "the certifier"} on ${formatWhen(c.decided_at)}.`;
    // sentToLodge: it has gone to Complete Law Support to lodge, so it isn't sent twice.
    return c.lodge_requested_at
      ? { state: "certified", canLodge: true, sentToLodge: true, message: `${certified} Sent to Complete Law Support to lodge on ${formatWhen(c.lodge_requested_at)}.` }
      : { state: "certified", canLodge: true, sentToLodge: false, message: certified };
  }
  return { state: "not_sent", canLodge: false, message: "This application must be certified before it can be lodged." };
}

/**
 * The Guideline Certification form's answers, taken from the application: the guideline and
 * criteria ticked at question 21, and the client and lawyer from the rest of the form. This
 * is what the Guideline Certification document sent to the certifier is built from.
 */
export function guidelineCertificationFromApplication(formData, today = new Date()) {
  return {
    client_name: [formData.first_name, formData.last_name].filter(Boolean).join(" "),
    client_dob: formData.date_of_birth || "",
    file_ref: formData.declaration_file_reference || "",
    lawyer: formData.lawyer_name || formData.preferred_lawyer || "",
    // The senior sign-off is recorded by the portal when the certifier certifies.
    certified_by: "To be certified in the portal",
    cert_date: today.toISOString().slice(0, 10),
    guideline: formData.vla_guideline_code || "",
    criteria: { ...(formData.guideline_criteria || {}) },
    waiver_applies: Boolean(formData.guideline_waiver_applies),
    notes: formData.guideline_notes || "",
  };
}

export function formatWhen(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

async function call(method, path, body) {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data;
}

/**
 * The certification for one application. Where certification isn't served at all (the
 * offline demo, or before it is configured) it reports `unavailable` and lodging is not
 * held up; any other failure holds lodging until it can be checked.
 */
export async function fetchCertificationStatus({ brand, applicationId, fingerprint }) {
  const params = new URLSearchParams({ brand: brand || "" });
  if (applicationId) params.set("application_id", applicationId);
  if (fingerprint) params.set("hash", fingerprint);
  try {
    return await call("GET", `/api/certifications?${params}`);
  } catch (error) {
    if (error.status === 404 || error.status === 503) return { unavailable: true };
    return { error: `Couldn't check certification: ${error.message}` };
  }
}

export const submitCertification = (body) => call("POST", "/api/certifications", body);
/** Send a certified application to Complete Law Support to lodge. */
export const lodgeCertification = (id, body) =>
  call("POST", `/api/certifications?action=lodge&id=${encodeURIComponent(id)}`, body);
export const decideCertification = (id, decision, note) =>
  call("PATCH", `/api/certifications?id=${encodeURIComponent(id)}`, { decision, note });
export const listCertifications = (brand) =>
  call("GET", `/api/certifications?view=review&brand=${encodeURIComponent(brand || "")}`);

export const listCertifiers = (brand) => call("GET", `/api/certifiers?brand=${encodeURIComponent(brand || "")}`);
export const addCertifier = (brand, email, name) => call("POST", "/api/certifiers", { brand, email, name });
export const removeCertifier = (id) => call("DELETE", `/api/certifiers?id=${encodeURIComponent(id)}`);
